import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createEmptyQuestion } from '../mock';

const QuizCtx = createContext(null);

const LS_CURRENT = 'moodleGen.current';
const LS_HISTORY = 'moodleGen.history';
const LS_LANG    = 'moodleGen.lang';
const LS_THEME   = 'moodleGen.theme';

export const QuizProvider = ({ children }) => {
  const [quizTitle, setQuizTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [lang, setLang] = useState('id');
  const [theme, setTheme] = useState('light');
  const [history, setHistory] = useState([]);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const cur = JSON.parse(localStorage.getItem(LS_CURRENT) || 'null');
      if (cur) {
        setQuizTitle(cur.quizTitle || '');
        setQuestions(cur.questions || []);
      }
      const hist = JSON.parse(localStorage.getItem(LS_HISTORY) || '[]');
      setHistory(Array.isArray(hist) ? hist : []);
      const l = localStorage.getItem(LS_LANG);
      if (l) setLang(l);
      const th = localStorage.getItem(LS_THEME);
      if (th) setTheme(th);
    } catch (e) { /* ignore */ }
  }, []);

  // Persist current draft on any change
  useEffect(() => {
    localStorage.setItem(LS_CURRENT, JSON.stringify({ quizTitle, questions }));
  }, [quizTitle, questions]);

  useEffect(() => { localStorage.setItem(LS_LANG, lang); }, [lang]);
  useEffect(() => {
    localStorage.setItem(LS_THEME, theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const addQuestion = useCallback((type) => {
    setQuestions(prev => {
      const sameTypeCount = prev.filter(q => q.type === type).length + 1;
      return [...prev, createEmptyQuestion(type, sameTypeCount)];
    });
  }, []);

  const updateQuestion = useCallback((id, patch) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, ...patch } : q));
  }, []);

  const removeQuestion = useCallback((id) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setQuizTitle('');
    setQuestions([]);
  }, []);

  const saveToHistory = useCallback(() => {
    const entry = {
      id: `h_${Date.now()}`,
      savedAt: new Date().toISOString(),
      quizTitle: quizTitle || 'Untitled',
      questions,
    };
    setHistory(prev => {
      const next = [entry, ...prev].slice(0, 30);
      localStorage.setItem(LS_HISTORY, JSON.stringify(next));
      return next;
    });
    return entry;
  }, [quizTitle, questions]);

  const loadFromHistory = useCallback((id) => {
    const entry = history.find(h => h.id === id);
    if (!entry) return;
    setQuizTitle(entry.quizTitle || '');
    setQuestions(entry.questions || []);
  }, [history]);

  const deleteHistory = useCallback((id) => {
    setHistory(prev => {
      const next = prev.filter(h => h.id !== id);
      localStorage.setItem(LS_HISTORY, JSON.stringify(next));
      return next;
    });
  }, []);

  const importQuestions = useCallback((qs) => {
    setQuestions(prev => [...prev, ...qs]);
  }, []);

  return (
    <QuizCtx.Provider value={{
      quizTitle, setQuizTitle,
      questions, setQuestions,
      addQuestion, updateQuestion, removeQuestion,
      clearAll,
      history, saveToHistory, loadFromHistory, deleteHistory,
      importQuestions,
      lang, setLang,
      theme, setTheme,
    }}>
      {children}
    </QuizCtx.Provider>
  );
};

export const useQuiz = () => {
  const ctx = useContext(QuizCtx);
  if (!ctx) throw new Error('useQuiz must be used within QuizProvider');
  return ctx;
};
